import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import React, { useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { HubGrid } from '@/components/insights-hub/hub-grid';
import { INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import { InsightsHubStackParams } from '@/navigators/types';
import { OfflineComponent } from '@/components/offline-component';
import { PageContainer } from '@/components/page-container';
import {
  Platform,
  ScrollView,
  StatusBar,
  TouchableOpacity,
} from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';

type Props = StackScreenProps<InsightsHubStackParams, INSIGHTS_HUB_ROUTES.Base>;

export const InsightsHubScreen: React.FC<Props> = () => {
  const { goBack } = useNavigation();
  const { isLargeLayout } = useLayoutContext();
  const isOfflineModeActive = useOfflineModeActive();
  const [styles, theme] = useStyles();

  const scrollRef = useRef<ScrollView>(null);

  const handleScrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ animated: true, y: 0 });
    }
  };

  /**
   * Show the navigation header only on mobile devices & tablets.
   */
  const showNavigationHeader = Platform.OS !== 'web' || !isLargeLayout;

  if (isOfflineModeActive) {
    return <OfflineComponent />;
  }

  return (
    <>
      <StatusBar barStyle="dark-content" translucent />
      <PageContainer
        containerStyle={styles.container}
        navigationHeaderProps={{
          BackButtonIcon: () => (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={goBack}
              style={styles.backButton}
            >
              <ArrowLeftThin fill={theme.sk.color.icon.primary} />
            </TouchableOpacity>
          ),
          containerStyle: styles.headerContainerStyle,
          isBlurred: !isLargeLayout,
          onHeaderPress: handleScrollToTop,
          showBackButton: true,
          showShadow: false,
        }}
        {...(!showNavigationHeader ? { showNavigationHeader: false } : {})}
      >
        <HubGrid scrollRef={scrollRef} />
      </PageContainer>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  backButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 16,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  container: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  headerContainerStyle: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
}));
